import arrowUpIcon from "@assets/icons/arrow_up.svg";
import avatarSpinner from "@assets/images/avatar_spinner.svg";
import ActivityCard from "@components/ActivityCard";
import AppBar from "@components/AppBar";
import InteractiveChatHistory, {
  IAvatarChatHistoryMessage,
  AvatarChatHistoryMessageSource
} from "@components/InteractiveChat/components/InteractiveChatHistory/InteractiveChatHistory";
import { InteractiveChatTextInput } from "@components/InteractiveChat/InteractiveChatTextInput";
import LocationPermissionModal from "@components/LocationPermissionModal";
import ROUTES from "@constants/routing";
import { InputContext } from "@context/Input/InputContext";
import { UserContext } from "@context/User/UserContext";
import { useHeaderHeight } from "@hooks/useHeaderHeight";
import LayoutDefault from "@layouts/LayoutDefault";
import { Box, Typography, IconButton } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import React from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useLocation } from "react-router-dom";

import CombinationsFilterSection from "./CombinationsFilterSection";
import { FILTER_SELECTION_LIMITS, canAddSelection } from "./filterLimits";
import SelectedItemPills from "./SelectedItemPills";
import { useCombinationsFlow } from "./useCombinationsFlow";
import { useFilterConfig, FilterType } from "./useFilterConfig";
import { useFilterValidation } from "./useFilterValidation";
import { useSelectionMessage } from "./useSelectionMessage";

/**
 * FilterSectionView Component
 *
 * Displays filter selection UI for different filter types:
 * - activity: Single category filter (activities with icons)
 * - day: Single category filter (time periods)
 * - location: Single category filter (geographic areas)
 * - combinations: Multi-category filter requiring:
 *   - At least one activity (required)
 *   - Optional locations
 *   - Optional days
 *
 * The combinations filter uses a separate state structure to handle
 * the different validation rules and message building requirements.
 */
const FilterSectionView: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const theme = useTheme();
  const inputCtx = React.useContext(InputContext);
  const userCtx = React.useContext(UserContext);
  const headerHeight = useHeaderHeight();

  // Get filter type from URL params or location state
  // Validate that it's a valid FilterType, defaulting to "activity" if invalid
  const urlParams = new URLSearchParams(location.search);
  const rawFilterType =
    urlParams.get("type") || location.state?.filterType || "activity";

  // Type guard to ensure filterType is valid
  const isValidFilterType = (value: string): value is FilterType => {
    return ["activity", "day", "location", "combinations"].includes(value);
  };

  const filterType: FilterType = isValidFilterType(rawFilterType)
    ? rawFilterType
    : "activity";

  // Local state for the chat input
  // NOTE: We use separate state structures because combinations has different requirements:
  // - Combinations: Requires grouped state (activities/locations/days) for validation and message building
  // - Regular filters: Simple array is sufficient
  const [selectedItems, setSelectedItems] = React.useState<string[]>([]);

  // Use the flow hook for combinations filter
  const combinationsFlow = useCombinationsFlow();
  const combinationsSelections = combinationsFlow.selections;

  // State for text input and audio when no selections are made
  const [textInput, setTextInput] = React.useState<string>("");
  const [audioBlob, setAudioBlob] = React.useState<Blob | null>(null);
  const [showLocationModal, setShowLocationModal] = React.useState(false);
  const [locationPermissionDenied, setLocationPermissionDenied] =
    React.useState(false);

  // Hooks for extracted logic
  const { buildRegularFilterMessage, transformMessageForBackend } =
    useSelectionMessage();
  const { isValid, hasAnySelection } = useFilterValidation();

  // Calculate validation state once to avoid repeated calls
  const isFormValid = isValid(
    filterType,
    selectedItems,
    combinationsSelections
  );
  const hasSelections = hasAnySelection(
    filterType,
    selectedItems,
    combinationsSelections
  );

  const handleSubmitFromInput = () => {
    if (!inputCtx) {
      console.error(
        "InputContext is not available. Ensure FilterSectionView is wrapped in InputProvider."
      );
      return;
    }

    // Build the display message (with text) and backend message (with numbers)
    const displayMessage =
      filterType === "combinations"
        ? combinationsFlow.buildFinalMessage()
        : buildRegularFilterMessage(
            filterType,
            selectedItems,
            filterConfig.translationKey
          );

    // Transform message for backend: replace date text with numeric ranges
    let backendMessage = displayMessage;
    if (filterType === "combinations") {
      backendMessage =
        combinationsFlow.transformFinalMessageForBackend(displayMessage);
    } else {
      backendMessage = transformMessageForBackend(
        displayMessage,
        filterType,
        selectedItems,
        filterConfig.translationKey
      );
    }

    // If display and backend are different (dates were transformed), use setInitialMessages
    // Otherwise, use setInitialMessage for backward compatibility
    if (displayMessage !== backendMessage) {
      inputCtx.setInitialMessages(displayMessage, backendMessage);
    } else {
      inputCtx.setInitialMessage(displayMessage);
    }
    inputCtx.setInitialAudio(null);
    navigate(ROUTES.CHATBOT, { replace: true });
  };

  // Handle text/audio submission when no selections are made
  const handleTextSubmit = () => {
    if (!inputCtx) {
      console.error(
        "InputContext is not available. Ensure FilterSectionView is wrapped in InputProvider."
      );
      return;
    }

    if (textInput.trim() === "" && !audioBlob) return;

    inputCtx.setInitialMessage(textInput.trim() || "");
    inputCtx.setInitialAudio(audioBlob);
    setTextInput("");
    setAudioBlob(null);
    navigate(ROUTES.CHATBOT, { replace: true });
  };

  const handleAudioRecord = (blob: Blob) => {
    setAudioBlob(blob);
  };

  const handleDeleteAudio = () => {
    setAudioBlob(null);
  };

  const handleStop = () => {
    // For now, just clear the input state
    // In the future, this could cancel an ongoing request
    setTextInput("");
    setAudioBlob(null);
  };

  /**
   * Handle geolocation errors by differentiating between:
   * - PERMISSION_DENIED (user has explicitly denied permission)
   * - TIMEOUT / POSITION_UNAVAILABLE (we couldn't get the location now)
   *
   * For the second case we show a specific message so the user
   * doesn't think it's a permissions problem.
   */
  const handleGeolocationError = (error: any) => {
    const errorCode =
      error && typeof error.code === "number" ? (error.code as number) : null;

    // 1 = PERMISSION_DENIED in the Geolocation API
    if (errorCode === 1) {
      setLocationPermissionDenied(true);
      setShowLocationModal(true);
      userCtx?.setLocationPermission("denied");
      return;
    }

    // Any other error (timeout, unavailable, etc.)
    // Show a clearer, localized message instead of a permissions popup
    alert(t("location.error.unavailable"));
  };

  // Helper function to check geolocation permissions
  const checkLocationPermission = async (itemKey: string): Promise<boolean> => {
    if (itemKey !== "mi_localizacion") {
      return true; // Not a location permission check
    }

    // Check if geolocation is supported
    if (!navigator.geolocation) {
      setShowLocationModal(true);
      return false;
    }

    // Check permission status using Permissions API if available
    if ("permissions" in navigator) {
      try {
        const permissionStatus = await navigator.permissions.query({
          name: "geolocation"
        });

        if (permissionStatus.state === "denied") {
          // Permission was denied, show modal and don't select
          setLocationPermissionDenied(true);
          setShowLocationModal(true);
          userCtx?.setLocationPermission("denied");
          return false;
        }

        if (permissionStatus.state === "prompt") {
          // Permission not yet asked, try to get location (will show browser prompt)
          return new Promise((resolve) => {
            navigator.geolocation.getCurrentPosition(
              () => {
                // Permission granted, update context
                userCtx?.setLocationPermission("granted");
                resolve(true);
              },
              (error) => {
                handleGeolocationError(error);
                resolve(false);
              },
              {
                enableHighAccuracy: false,
                timeout: 8000,
                maximumAge: 300000 // 5 minutes
              }
            );
          });
        }

        // Permission already granted, update context to ensure it's synced
        userCtx?.setLocationPermission("granted");
        return true;
      } catch {
        // Permissions API not supported or failed, try to get location directly
        return new Promise((resolve) => {
          navigator.geolocation.getCurrentPosition(
            () => {
              // Permission granted, update context
              userCtx?.setLocationPermission("granted");
              resolve(true);
            },
            (error) => {
              handleGeolocationError(error);
              resolve(false);
            },
            {
              enableHighAccuracy: false,
              timeout: 8000,
              maximumAge: 300000 // 5 minutes
            }
          );
        });
      }
    } else {
      // Permissions API not supported, try to get location directly
      return new Promise((resolve) => {
        navigator.geolocation.getCurrentPosition(
          () => {
            // Permission granted, update context
            userCtx?.setLocationPermission("granted");
            resolve(true);
          },
          (error) => {
            handleGeolocationError(error);
            resolve(false);
          },
          {
            enableHighAccuracy: false,
            timeout: 8000,
            maximumAge: 300000 // 5 minutes
          }
        );
      });
    }
  };

  const handleItemSelect = async (
    itemKey: string,
    category?: "activities" | "locations" | "days"
  ) => {
    // Clear text and audio when a pill is selected
    setTextInput("");
    setAudioBlob(null);

    // Check location permission if selecting "mi_localizacion"
    if (itemKey === "mi_localizacion") {
      const hasPermission = await checkLocationPermission(itemKey);
      if (!hasPermission) {
        // Deselect if permission denied
        if (filterType === "combinations" && category) {
          // Remove from combinations if already selected
          if (combinationsSelections.locations.includes(itemKey)) {
            combinationsFlow.removeItem(itemKey, category);
          }
        } else {
          // Remove from regular selection if already selected
          setSelectedItems((prev: string[]) =>
            prev.filter((key: string) => key !== itemKey)
          );
        }
        return;
      }
    }

    if (filterType === "combinations" && category) {
      combinationsFlow.handleItemSelect(itemKey, category);
    } else {
      // For individual filters (activity, day, location), apply selection limits
      setSelectedItems((prev: string[]) => {
        // If item is already selected, deselect it
        if (prev.includes(itemKey)) {
          return prev.filter((key: string) => key !== itemKey);
        }

        const limit =
          FILTER_SELECTION_LIMITS[
            filterType as "activity" | "day" | "location"
          ] ?? null;
        if (!canAddSelection(prev, limit)) {
          return prev;
        }

        return [...prev, itemKey];
      });
    }
  };

  const clearSelection = () => {
    if (filterType === "combinations") {
      combinationsFlow.clearAll();
    } else {
      setSelectedItems([]);
    }
  };

  const removeItem = (
    itemKey: string,
    category?: "activities" | "locations" | "days"
  ) => {
    if (filterType === "combinations" && category) {
      combinationsFlow.removeItem(itemKey, category);
    } else {
      setSelectedItems((prev: string[]) =>
        prev.filter((key: string) => key !== itemKey)
      );
    }
  };

  const renderItemPills = () => {
    if (filterType === "combinations") {
      // Combine all selections with their categories
      const allSelections: Array<{
        key: string;
        category: "activities" | "locations" | "days";
        translationKey: string;
      }> = [];

      combinationsSelections.activities.forEach((key: string) => {
        allSelections.push({
          key,
          category: "activities",
          translationKey: "filter_section"
        });
      });
      combinationsSelections.locations.forEach((key: string) => {
        allSelections.push({
          key,
          category: "locations",
          translationKey: "filter_location"
        });
      });
      combinationsSelections.days.forEach((key: string) => {
        allSelections.push({
          key,
          category: "days",
          translationKey: "filter_day"
        });
      });

      return (
        <SelectedItemPills
          selectedItems={allSelections.map((s) => s.key)}
          translationKey={filterConfig.translationKey}
          onRemove={(key: string) => {
            const selection = allSelections.find((s) => s.key === key);
            if (selection) {
              removeItem(key, selection.category);
            }
          }}
          onClear={clearSelection}
          selectionsWithCategory={allSelections}
        />
      );
    } else {
      return (
        <SelectedItemPills
          selectedItems={selectedItems}
          translationKey={filterConfig.translationKey}
          onRemove={removeItem}
          onClear={clearSelection}
        />
      );
    }
  };

  // Configuración dinámica mediante hook
  const filterConfig = useFilterConfig(filterType);
  // Get configs for combinations sections
  const activityConfig = useFilterConfig("activity");
  const locationConfig = useFilterConfig("location");
  const dayConfig = useFilterConfig("day");

  // Build dynamic chat messages based on filter type
  const mockMessages: IAvatarChatHistoryMessage[] = React.useMemo(() => {
    if (filterType === "combinations") {
      const flowMessages = combinationsFlow.buildConversationMessages();
      return flowMessages.map((msg) => ({
        source:
          msg.source === "user"
            ? AvatarChatHistoryMessageSource.USER
            : AvatarChatHistoryMessageSource.AVATAR,
        text: msg.text,
        timestamp: new Date()
      }));
    } else {
      return [
        {
          source: AvatarChatHistoryMessageSource.USER,
          text: filterConfig.userMessage,
          timestamp: new Date()
        },
        {
          source: AvatarChatHistoryMessageSource.AVATAR,
          text: filterConfig.botMessage,
          timestamp: new Date()
        }
      ];
    }
  }, [filterType, filterConfig, combinationsFlow]);

  return (
    <LayoutDefault title="diari-ara">
      <Box
        sx={{
          display: "flex",
          width: "100%",
          height: "100%",
          backgroundColor: theme.palette.background.default,
          backgroundSize: "cover",
          backgroundPosition: "center"
        }}
      >
        <Box
          width={"100%"}
          sx={{ display: "flex", flexDirection: "column", height: "100%" }}
        >
          <AppBar />

          {/* Main content area */}
          <Box
            sx={{
              flex: 1,
              overflow: "auto",
              display: "flex",
              flexDirection: "column",
              justifyContent: "flex-start",
              alignItems: "center",
              px: { xs: 2, sm: 4 },
              pb: { xs: 2, sm: 4 },
              boxSizing: "border-box",
              pt: {
                xs: headerHeight.mobile,
                sm: headerHeight.desktop
              }
            }}
          >
            {/* Chat History - Top */}
            <Box
              sx={{ width: "100%", maxWidth: { xs: "100%", sm: 800 }, mb: 0 }}
            >
              <InteractiveChatHistory messages={mockMessages} noSidePadding />
            </Box>

            {/* Filter Selection - Middle */}
            <Box
              sx={{
                width: "100%",
                maxWidth: { xs: "100%", sm: 800 },
                mb: 0,
                mt: -0.5,
                pt: 0.5
              }}
            >
              {filterType === "combinations" ? (
                <>
                  <CombinationsFilterSection
                    selections={combinationsSelections}
                    currentStep={combinationsFlow.currentStep}
                    showPills={combinationsFlow.showPills}
                    onItemSelect={(itemKey, category) =>
                      handleItemSelect(itemKey, category)
                    }
                    activityConfig={activityConfig}
                    locationConfig={locationConfig}
                    dayConfig={dayConfig}
                  />
                  {/* Show spinner while waiting (3 seconds delay) */}
                  {combinationsFlow.isWaiting && (
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "flex-start",
                        gap: 1,
                        py: 1,
                        px: 0
                      }}
                    >
                      <img
                        src={avatarSpinner}
                        alt="Avatar"
                        style={{
                          width: 30,
                          height: 30,
                          borderRadius: "50%",
                          marginTop: 0,
                          animation: "spin 1s linear infinite"
                        }}
                      />
                    </Box>
                  )}
                </>
              ) : (
                <>
                  <Box
                    sx={{
                      display: "flex",
                      flexWrap: "wrap",
                      gap: 1.5,
                      justifyContent: "flex-start"
                    }}
                  >
                    {filterConfig.options.map((option) => (
                      <ActivityCard
                        key={option.key}
                        title={t(
                          `${filterConfig.translationKey}.${option.key}`
                        )}
                        icon={
                          option.iconSrc ? (
                            <img
                              src={option.iconSrc}
                              alt={option.key}
                              style={{ width: 18, height: 18 }}
                            />
                          ) : undefined
                        }
                        selected={selectedItems.includes(option.key)}
                        onClick={() => handleItemSelect(option.key)}
                      />
                    ))}
                  </Box>
                </>
              )}
            </Box>
          </Box>

          {/* Chat Input - Fixed at bottom */}
          <Box
            sx={{
              width: "100%",
              maxWidth: { xs: "100%", sm: 800 },
              mx: "auto",
              p: { xs: 2, sm: 4 },
              pt: { xs: 1, sm: 2 },
              flexShrink: 0
            }}
          >
            {filterType === "combinations" &&
            combinationsFlow.currentStep === "complete" ? (
              // Show final message input when flow is complete
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  bgcolor: theme.palette.background.paper,
                  borderRadius: "66px",
                  minHeight: 54,
                  px: 2,
                  py: 1,
                  gap: 1,
                  border: `1px solid ${theme.palette.customColors.inputBorder}`,
                  boxShadow: theme.palette.customColors.shadowLow
                }}
              >
                <Typography
                  sx={{
                    fontSize: 14,
                    color: theme.palette.text.primary,
                    ml: 1,
                    flex: 1,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap"
                  }}
                >
                  {combinationsFlow.buildFinalMessage()}
                </Typography>

                {/* Send button */}
                <IconButton
                  onClick={handleSubmitFromInput}
                  sx={{
                    bgcolor: theme.palette.customColors.textPrimary,
                    color: theme.palette.common.white,
                    width: 38,
                    height: 38,
                    "&:hover": {
                      bgcolor: theme.palette.customColors.textSecondary
                    }
                  }}
                >
                  <img
                    src={arrowUpIcon}
                    alt="Send"
                    style={{
                      width: 20,
                      height: 20,
                      filter: "brightness(0) invert(1)"
                    }}
                  />
                </IconButton>
              </Box>
            ) : hasSelections ? (
              // Show pills input when selections are made (for non-combinations or incomplete combinations)
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  bgcolor: theme.palette.background.paper,
                  borderRadius: "66px",
                  minHeight: 54,
                  px: 2,
                  py: 1,
                  gap: 1,
                  border: `1px solid ${theme.palette.customColors.inputBorder}`,
                  boxShadow: theme.palette.customColors.shadowLow
                }}
              >
                {/* Pills */}
                <Box
                  sx={{
                    flex: 1,
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    flexWrap: "wrap",
                    overflow: "hidden"
                  }}
                >
                  {renderItemPills()}
                </Box>

                {/* Send button */}
                <IconButton
                  onClick={handleSubmitFromInput}
                  disabled={!isFormValid}
                  sx={{
                    bgcolor: isFormValid
                      ? theme.palette.customColors.textPrimary
                      : theme.palette.customColors.chipBg,
                    color: isFormValid
                      ? theme.palette.common.white
                      : theme.palette.text.primary,
                    width: 38,
                    height: 38,
                    "&:hover": {
                      bgcolor: isFormValid
                        ? theme.palette.customColors.textSecondary
                        : theme.palette.customColors.inputBorder
                    },
                    "&:disabled": {
                      bgcolor: theme.palette.customColors.chipBg,
                      color: theme.palette.text.disabled,
                      cursor: "not-allowed"
                    }
                  }}
                >
                  <img
                    src={arrowUpIcon}
                    alt="Send"
                    style={{
                      width: 20,
                      height: 20,
                      filter: isFormValid ? "brightness(0) invert(1)" : "none"
                    }}
                  />
                </IconButton>
              </Box>
            ) : (
              // Show InteractiveChatTextInput when no selections are made
              <InteractiveChatTextInput
                value={textInput}
                onChange={setTextInput}
                onSubmit={handleTextSubmit}
                onRecord={handleAudioRecord}
                audioBlob={audioBlob}
                onDeleteAudio={handleDeleteAudio}
                onStop={handleStop}
                label={t(
                  "chatbot.common.input_placeholder",
                  "Hazme una pregunta..."
                )}
                placeholder={t(
                  "chatbot.common.input_placeholder",
                  "Hazme una pregunta..."
                )}
                disabled={false}
                loading={false}
              />
            )}

            {/* Disclaimer text */}
            <Box sx={{ px: 0, mt: 1 }}>
              <Typography
                sx={{
                  fontFamily: theme.typography.fontFamily,
                  fontSize: "14px",
                  fontWeight: 325,
                  lineHeight: "14px",
                  letterSpacing: "0%",
                  color: theme.palette.customColors.textTertiary,
                  textAlign: "center"
                }}
              >
                {t("chatbot.disclaimer")}
              </Typography>
            </Box>
          </Box>
        </Box>
      </Box>

      {/* Location Permission Modal */}
      <LocationPermissionModal
        open={showLocationModal}
        isDenied={locationPermissionDenied}
        onAccept={async () => {
          setShowLocationModal(false);
          setLocationPermissionDenied(false);
          // Try to get location after user accepts
          if (navigator.geolocation) {
            navigator.geolocation.getCurrentPosition(
              () => {
                // Permission granted, update context and can now select "mi_localizacion"
                userCtx?.setLocationPermission("granted");
              },
              () => {
                // Still denied, user needs to enable in browser settings
                setLocationPermissionDenied(true);
                userCtx?.setLocationPermission("denied");
              },
              {
                enableHighAccuracy: false,
                timeout: 8000,
                maximumAge: 300000 // 5 minutes
              }
            );
          }
        }}
        onClose={() => {
          setShowLocationModal(false);
          setLocationPermissionDenied(false);
        }}
      />
    </LayoutDefault>
  );
};

export default FilterSectionView;
